import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Redtext } from '../../../../generated-api';
import { MarkdownComponent } from 'ngx-markdown';
import { LinkHandlerService } from '../../../../core/services/markdown-link-handler.service';
import * as Constants from '../../../../core/helpers/constants/constants';
import titleReplacements from '../../../../../assets/config/title-replacements.json';
import { initSlideshows } from 'src/app/core/helpers/mdslideshow/slideshow';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'app-redtext-view',
  templateUrl: './redtext-view.component.html',
  styleUrls: ['./redtext-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class RedtextViewComponent implements OnChanges, OnDestroy {
  @Input() redtextData: Redtext;
  @Input() loading = false;
  @Input() error = null;

  // mockSwimlane = SWIMLANE_MOCK;
  swimlaneCopyright = ""
  swimlaneCopyrightTooltip = undefined;

  picture = null;
  routerLink = null;

  @ViewChild('mkMain') markdown3: MarkdownComponent;
  @ViewChild('mkBox1') markdown4: MarkdownComponent;
  @ViewChild('mkBox2') markdown5: MarkdownComponent;
  @ViewChild('mkBox3') markdown6: MarkdownComponent;

  seoScript: HTMLScriptElement
  seoScriptInitialized = false
  constructor(private linkHandler: LinkHandlerService,
    @Inject(DOCUMENT) private document: any,
    private renderer: Renderer2) {
    this.seoScript = this.renderer.createElement('script');
  }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.redtextData && changes.redtextData.currentValue) {
      this.picture = changes.redtextData.currentValue.image || Constants.PLACEHOLDER_IMAGE_WITH_PATH;
      this.routerLink = this.getRouterLink()

      initSlideshows(this.document);

      let copyrights = new Array<string>();
      if (this.redtextData.imagec) copyrights.push(this.redtextData.imagec)
      if (this.redtextData.swimlane != null) {
        for (let i = 0; i < this.redtextData.swimlane.items.length; i++) {
          let item = this.redtextData.swimlane.items[i];
          if (copyrights.indexOf(item.imagec) < 0) {
            copyrights.push(item.imagec)
          }
        }
      }
      this.swimlaneCopyright = Array.from(copyrights.values()).slice(0,6).join(", ");
      if (copyrights.length>6) {
        this.swimlaneCopyright += "...";
        this.swimlaneCopyrightTooltip = Array.from(copyrights.values()).join(", ");
      }

      // json-ld ala https://javascript.plainenglish.io/how-to-use-json-ld-for-advanced-seo-in-angular-63528c98bb91
      if (document) {
        let script;
        let shouldAppend = false;
        if (document.head.getElementsByClassName('structured-data').length) {
          script = document.head.getElementsByClassName('structured-data')[0];
        } else {
          script = document.createElement('script');
          shouldAppend = true;
        }
        script.setAttribute('class', 'structured-data');
        script.type = 'application/ld+json';

        if (this.redtextData != null) {
          let reviewBody = this.redtextData.content;
          if (this.redtextData.box3) reviewBody =  this.redtextData.box3 + '\n\n' + reviewBody;
          if (this.redtextData.box2) reviewBody =  this.redtextData.box2 + '\n\n' + reviewBody;
          if (this.redtextData.box1) reviewBody =  this.redtextData.box1 + '\n\n' + reviewBody;

          let title = this.redtextData.title
          if (!title && this.redtextData.rubrik.name) {
            title = this.redtextData.rubrik.name
          }

          script.text = JSON.stringify({
            '@context': 'http://schema.org',
            '@type': 'Article',
            'publisher': {
              '@type': 'Organization',
              'sameAs': '/redtext/101225/Herausgeber',
              'name': 'Programmberatung für Eltern e.V.'
            },
            'author': {
              '@type': 'Organization',
              'sameAs': '/redtext/101237/Redaktion',
              'name': 'JFF – Institut für Medienpädagogik in Forschung und Praxis'
            },
            'inLanguage': 'German',
            'url': '/redtext/' + this.redtextData.id + '/' + this.prettifyUrl(title),
            'datePublished': this.redtextData.publishedDate ?? '2025-04-23',
            'pagination': 1,
            'image': this.redtextData.image,
            'headline': this.redtextData.title,
            'description': this.redtextData.content.substr(0, 198) + '..',
            'articleBody': reviewBody,
            'articleSection': this.redtextData.rubrik.name
          });
        }
        if (shouldAppend) {
          document.head.appendChild(script);
        }
      }
    }
  }

  prettifyUrl(title: string): string {
    if (!title) title = '';
    let prettyString = title.toLocaleLowerCase();
    titleReplacements.forEach(val => {
      prettyString = prettyString.split(val.from).join(val.to);
    });
    return prettyString;
  }

  onImgError(): void {
    this.picture = Constants.PLACEHOLDER_IMAGE_WITH_PATH;
  }

  ngOnDestroy(): void {
  }

  mkReady(element): void {
    this.linkHandler.wireInsertedLinks(element.element);
  }

  getFileName(pdfuri: string): string {
    const filename = pdfuri.split('/').pop();
    return filename.substring(0, filename.lastIndexOf('.'));
  }

  getRouterLink(): string {
    if (this.redtextData && this.redtextData.rubrik) {
      switch (this.redtextData.rubrik.id) {
        case 1001:
          return '/ueber-flimmo';
        case 1:
          return '/redtext/101488/Medienerziehung';
        case 2:
          return '/redtext/101489/Streaming';
        case 3:
          return '/redtext/101472/YouTube,-TikTok---Instagram';
        default:
          return null;
      }
    } else {
      return null;
    }
  }

  openUri(pdfuri: string): void {
    window.open(pdfuri, '_blank');
  }
}
